import React from "react";

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M16.704 5.29a1 1 0 010 1.42l-7.2 7.2a1 1 0 01-1.414 0l-3.2-3.2a1 1 0 011.414-1.42l2.493 2.494 6.493-6.494a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l6.58 11.69c.75 1.334-.213 2.985-1.743 2.985H3.42c-1.53 0-2.493-1.651-1.743-2.985l6.58-11.69zM10 7a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 7zm0 7.25a1 1 0 100-2 1 1 0 000 2z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M18 10A8 8 0 112 10a8 8 0 0116 0zM9.25 8.5a.75.75 0 011.5 0v4.75a.75.75 0 01-1.5 0V8.5zM10 6.75a1 1 0 100-2 1 1 0 000 2z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function getStatus(result) {
  const value = String(
    result?.status ??
      result?.validationStatus ??
      ""
  )
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (
    [
      "valid",
      "success",
      "passed",
      "pass",
      "ok",
      "valid_blueprint",
    ].includes(value)
  ) {
    return "valid";
  }

  if (
    [
      "warning",
      "warnings",
      "partial",
      "valid_with_warnings",
    ].includes(value)
  ) {
    return "warning";
  }

  if (
    [
      "invalid",
      "error",
      "errors",
      "failed",
      "fail",
    ].includes(value)
  ) {
    return "invalid";
  }

  if (result?.valid === true) {
    return "valid";
  }

  if (result?.valid === false) {
    return "invalid";
  }

  return "unknown";
}

function getCount(result, keys) {
  for (const key of keys) {
    if (
      result?.[key] !== undefined &&
      result?.[key] !== null
    ) {
      const value = Number(
        result[key]
      );

      if (Number.isFinite(value)) {
        return value;
      }
    }
  }

  return null;
}

function getArray(result, keys) {
  for (const key of keys) {
    if (Array.isArray(result?.[key])) {
      return result[key];
    }
  }

  return [];
}

function Stat({
  label,
  value,
  tone,
  icon,
}) {
  const styles = {
    green: {
      wrapper:
        "border-emerald-100 bg-emerald-50",
      icon: "bg-emerald-100 text-emerald-600",
      value: "text-emerald-700",
    },
    red: {
      wrapper: "border-red-100 bg-red-50",
      icon: "bg-red-100 text-red-600",
      value: "text-red-700",
    },
    amber: {
      wrapper:
        "border-amber-100 bg-amber-50",
      icon: "bg-amber-100 text-amber-600",
      value: "text-amber-700",
    },
    blue: {
      wrapper:
        "border-blue-100 bg-blue-50",
      icon: "bg-blue-100 text-blue-600",
      value: "text-blue-700",
    },
  };

  const style =
    styles[tone] || styles.blue;

  return (
    <div
      className={`rounded-xl border p-3 ${style.wrapper}`}
    >
      <div
        className={`flex h-8 w-8 items-center justify-center rounded-lg ${style.icon}`}
      >
        {icon}
      </div>

      <p className="mt-3 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </p>

      <p
        className={`mt-1 text-xl font-bold ${style.value}`}
      >
        {value}
      </p>
    </div>
  );
}

export default function BlueprintValidationSummary({
  result = null,
  loading = false,
  error = null,
  title = "Validation Summary",
  description = "Overview of the latest English quiz blueprint validation.",
  compact = false,
  showChecks = true,
  showCoverage = true,
  showMessage = true,
  onValidate,
  onViewDetails,
}) {
  if (loading) {
    return (
      <div className="w-full rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
            <div className="mt-2 h-3 w-64 animate-pulse rounded bg-gray-100" />
          </div>

          <div className="h-8 w-20 animate-pulse rounded-lg bg-gray-100" />
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[1, 2, 3, 4].map(
            (item) => (
              <div
                key={item}
                className="h-24 animate-pulse rounded-xl bg-gray-100"
              />
            )
          )}
        </div>
      </div>
    );
  }

  if (error) {
    const message =
      typeof error === "string"
        ? error
        : error?.message ||
          error?.data?.message ||
          "Unable to retrieve blueprint validation results.";

    return (
      <div className="w-full rounded-xl border border-red-200 bg-red-50 p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600">
            <XIcon />
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-red-900">
              Validation unavailable
            </h3>

            <p className="mt-1 text-xs leading-5 text-red-700">
              {message}
            </p>

            {onValidate && (
              <button
                type="button"
                onClick={onValidate}
                className="mt-3 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-red-700"
              >
                Validate Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="w-full rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
            <InfoIcon />
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-gray-800">
              {title}
            </h3>

            <p className="mt-1 text-xs leading-5 text-gray-500">
              No validation result is available yet.
            </p>

            {onValidate && (
              <button
                type="button"
                onClick={onValidate}
                className="mt-3 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-indigo-700"
              >
                Validate Blueprint
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const status = getStatus(result);

  const errors =
    getArray(result, [
      "errors",
      "validationErrors",
    ]);

  const warnings =
    getArray(result, [
      "warnings",
      "validationWarnings",
    ]);

  const errorCount =
    getCount(result, [
      "errorCount",
      "errorsCount",
    ]) ?? errors.length;

  const warningCount =
    getCount(result, [
      "warningCount",
      "warningsCount",
    ]) ?? warnings.length;

  const totalChecks =
    getCount(result, [
      "totalChecks",
      "checkCount",
      "validatedChecks",
    ]);

  const passedChecks =
    getCount(result, [
      "passedChecks",
      "successfulChecks",
      "validChecks",
    ]);

  const failedChecks =
    getCount(result, [
      "failedChecks",
      "invalidChecks",
    ]) ?? errorCount;

  const coverage =
    getCount(result, [
      "coverage",
      "availabilityPercentage",
      "completionPercentage",
    ]);

  const statusConfig = {
    valid: {
      label: "Valid",
      description:
        "The blueprint passed validation.",
      wrapper:
        "border-emerald-200 bg-emerald-50",
      icon:
        "bg-emerald-100 text-emerald-600",
      title:
        "text-emerald-900",
      text:
        "text-emerald-700",
      symbol: <CheckIcon />,
    },
    warning: {
      label: "Review",
      description:
        "The blueprint is valid with warnings.",
      wrapper:
        "border-amber-200 bg-amber-50",
      icon:
        "bg-amber-100 text-amber-600",
      title:
        "text-amber-900",
      text:
        "text-amber-700",
      symbol: <WarningIcon />,
    },
    invalid: {
      label: "Invalid",
      description:
        "The blueprint has validation errors.",
      wrapper:
        "border-red-200 bg-red-50",
      icon: "bg-red-100 text-red-600",
      title: "text-red-900",
      text: "text-red-700",
      symbol: <XIcon />,
    },
    unknown: {
      label: "Not Checked",
      description:
        "The blueprint has not been fully validated.",
      wrapper:
        "border-blue-200 bg-blue-50",
      icon: "bg-blue-100 text-blue-600",
      title: "text-blue-900",
      text: "text-blue-700",
      symbol: <InfoIcon />,
    },
  }[status];

  const message =
    result.message ||
    result.summary ||
    statusConfig.description;

  const calculatedCoverage =
    coverage !== null
      ? Math.max(
          0,
          Math.min(100, coverage)
        )
      : totalChecks &&
        totalChecks > 0 &&
        passedChecks !== null
      ? Math.max(
          0,
          Math.min(
            100,
            (passedChecks /
              totalChecks) *
              100
          )
        )
      : null;

  return (
    <div className="w-full">
      <div
        className={`rounded-xl border p-4 ${statusConfig.wrapper}`}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <div
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${statusConfig.icon}`}
            >
              {statusConfig.symbol}
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3
                  className={`text-sm font-semibold ${statusConfig.title}`}
                >
                  {title}
                </h3>

                <span
                  className={`rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${statusConfig.text}`}
                >
                  {statusConfig.label}
                </span>
              </div>

              {description && (
                <p
                  className={`mt-1 text-xs ${statusConfig.text}`}
                >
                  {description}
                </p>
              )}
            </div>
          </div>

          <div className="flex shrink-0 gap-2">
            {onValidate && (
              <button
                type="button"
                onClick={onValidate}
                className={`rounded-lg border border-white/70 bg-white/70 px-3 py-2 text-xs font-semibold transition hover:bg-white ${statusConfig.title}`}
              >
                Validate Again
              </button>
            )}

            {onViewDetails && (
              <button
                type="button"
                onClick={onViewDetails}
                className="rounded-lg bg-white/70 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-white"
              >
                View Details
              </button>
            )}
          </div>
        </div>

        {showMessage && (
          <div className="mt-4 rounded-lg bg-white/60 px-3 py-2.5">
            <p
              className={`text-xs leading-5 ${statusConfig.text}`}
            >
              {message}
            </p>
          </div>
        )}
      </div>

      <div
        className={`mt-3 grid gap-3 ${
          compact
            ? "grid-cols-2"
            : "grid-cols-2 sm:grid-cols-4"
        }`}
      >
        <Stat
          label="Status"
          value={statusConfig.label}
          tone={
            status === "valid"
              ? "green"
              : status === "warning"
              ? "amber"
              : status === "invalid"
              ? "red"
              : "blue"
          }
          icon={
            status === "valid" ? (
              <CheckIcon />
            ) : status === "warning" ? (
              <WarningIcon />
            ) : status === "invalid" ? (
              <XIcon />
            ) : (
              <InfoIcon />
            )
          }
        />

        <Stat
          label="Errors"
          value={errorCount}
          tone={
            errorCount > 0
              ? "red"
              : "green"
          }
          icon={
            errorCount > 0 ? (
              <XIcon />
            ) : (
              <CheckIcon />
            )
          }
        />

        <Stat
          label="Warnings"
          value={warningCount}
          tone={
            warningCount > 0
              ? "amber"
              : "green"
          }
          icon={
            warningCount > 0 ? (
              <WarningIcon />
            ) : (
              <CheckIcon />
            )
          }
        />

        {showChecks ? (
          <Stat
            label="Checks"
            value={
              totalChecks !== null
                ? `${passedChecks ?? 0}/${totalChecks}`
                : "—"
            }
            tone={
              failedChecks > 0
                ? "amber"
                : "green"
            }
            icon={
              failedChecks > 0 ? (
                <WarningIcon />
              ) : (
                <CheckIcon />
              )
            }
          />
        ) : (
          <Stat
            label="Issues"
            value={
              errorCount +
              warningCount
            }
            tone={
              errorCount > 0
                ? "red"
                : warningCount > 0
                ? "amber"
                : "green"
            }
            icon={
              errorCount > 0 ? (
                <XIcon />
              ) : warningCount > 0 ? (
                <WarningIcon />
              ) : (
                <CheckIcon />
              )
            }
          />
        )}
      </div>

      {showCoverage &&
        calculatedCoverage !==
          null && (
          <div className="mt-3 rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h4 className="text-xs font-semibold text-gray-800">
                  Validation Coverage
                </h4>

                <p className="mt-0.5 text-[10px] text-gray-500">
                  Percentage of validation checks that passed.
                </p>
              </div>

              <span
                className={`text-sm font-bold ${
                  calculatedCoverage >= 100
                    ? "text-emerald-600"
                    : calculatedCoverage >=
                      80
                    ? "text-amber-600"
                    : "text-red-600"
                }`}
              >
                {calculatedCoverage.toFixed(
                  0
                )}
                %
              </span>
            </div>

            <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100">
              <div
                className={`h-full rounded-full transition-all ${
                  calculatedCoverage >=
                  100
                    ? "bg-emerald-500"
                    : calculatedCoverage >=
                      80
                    ? "bg-amber-500"
                    : "bg-red-500"
                }`}
                style={{
                  width: `${calculatedCoverage}%`,
                }}
              />
            </div>
          </div>
        )}
    </div>
  );
}